import type { Decision, DecisionStatus, ObjectSnapshot, Question } from '../../api/uiSignoff';
import type { RowSpec, SheetSpec } from '../../lib/xlsx';
import type { ReadSheet } from '../../lib/xlsxRead';
import { day, DECISION_LABEL, picklistLabel, sectionTitle, SOURCE_LABEL, viewSections } from './viewModel';

// A "field dictionary" workbook: one row per field, with where it sits, what was agreed, and what
// is still being asked. Import maps one onto decisions and anchored questions; export produces one
// from the snapshot, so the spreadsheet becomes an output of the module rather than a second source.

export interface ImportDecision {
  fieldApi: string | null;
  proposedLabel: string | null;
  status: DecisionStatus;
  placement: string | null;
  note: string | null;
}
export interface ImportQuestion { anchorRef: string; question: string; answer: string | null }

export interface DictionaryParse {
  sheet: string;
  rows: number;
  decisions: ImportDecision[];
  questions: ImportQuestion[];
  /** Fields the workbook names that the snapshot does not define. */
  unknownFields: string[];
}

const API_RE = /^[A-Za-z][A-Za-z0-9_]*$/;
const clean = (s: string | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();

function statusOf(raw: string): DecisionStatus | null {
  const s = clean(raw).toLowerCase();
  if (!s) return null;
  if (s === 'on layout') return 'on_layout';
  if (s === 'admin only') return 'admin_only';
  if (s.startsWith('hidden')) return 'hidden_phase_2';
  if (s === 'not on layout') return 'not_on_layout';
  if (s === 'remove') return 'remove';
  if (s === 'check') return 'check';
  return null;
}

/**
 * Finds the sheet whose header row has "API Name" and "Agreed layout", and reads every row below
 * it. Section rows (no label, no API name) are skipped. A row whose API name isn't one (blank or
 * "(new)") is an agreed field not yet built, placed in its section. "Check" with nothing recorded
 * is the default state and is not imported.
 */
export function parseDictionary(sheets: ReadSheet[], fields: Record<string, unknown>): DictionaryParse | null {
  for (const sheet of sheets) {
    const h = sheet.rows.findIndex((r) => r.some((c) => clean(c).toLowerCase() === 'api name') && r.some((c) => clean(c).toLowerCase().startsWith('agreed layout')));
    if (h < 0) continue;
    const head = sheet.rows[h].map((c) => clean(c).toLowerCase());
    const col = (pred: (x: string) => boolean) => head.findIndex(pred);
    const cSection = col((x) => x === 'section');
    const cLabel = col((x) => x === 'field label' || x === 'label');
    const cApi = col((x) => x === 'api name');
    const cAgreed = col((x) => x.startsWith('agreed layout'));
    const cConfirmed = col((x) => x === 'confirmed');
    const cQuestion = col((x) => x.startsWith('question'));
    const cAnswer = col((x) => x.endsWith('answer'));

    const out: DictionaryParse = { sheet: sheet.name, rows: 0, decisions: [], questions: [], unknownFields: [] };
    for (const r of sheet.rows.slice(h + 1)) {
      const api = clean(r[cApi]);
      const label = cLabel >= 0 ? clean(r[cLabel]) : '';
      if (!api && !label) continue;
      out.rows++;
      const isField = API_RE.test(api);
      const section = cSection >= 0 ? clean(r[cSection]) : '';
      const placement = section.toLowerCase() === 'highlights' ? 'highlights' : section || null;
      const confirmed = cConfirmed >= 0 ? clean(r[cConfirmed]) : '';
      let status = statusOf(cAgreed >= 0 ? r[cAgreed] : '');
      if (!isField && !status) status = 'on_layout'; // a new field in the dictionary is one someone asked for
      if (status && !(status === 'check' && !confirmed)) {
        out.decisions.push({ fieldApi: isField ? api : null, proposedLabel: isField ? null : label || null, status, placement: isField ? null : placement, note: confirmed || null });
      } else if (!status && confirmed) {
        out.decisions.push({ fieldApi: isField ? api : null, proposedLabel: isField ? null : label || null, status: 'check', placement: isField ? null : placement, note: confirmed });
      }
      const q = cQuestion >= 0 ? clean(r[cQuestion]) : '';
      if (q) out.questions.push({ anchorRef: isField ? api : label, question: q, answer: cAnswer >= 0 && clean(r[cAnswer]) ? clean(r[cAnswer]) : null });
      if (isField && !(api in fields) && !out.unknownFields.includes(api)) out.unknownFields.push(api);
    }
    out.decisions = out.decisions.filter((d) => d.fieldApi || d.proposedLabel);
    return out;
  }
  return null;
}

/** The dictionary for one object, from the snapshot plus what has been decided and asked. */
export function dictionarySheets(o: ObjectSnapshot, current: Decision[], questions: Question[], meta: { repo: string; branch: string; commit: string; client: string }): SheetSpec[] {
  const byField = new Map(current.filter((d) => d.fieldApi).map((d) => [d.fieldApi!, d]));
  const qsFor = (ref: string) => questions.filter((q) => q.anchorRef === ref);

  // Where each field sits: highlights first, then sections in page order across all views.
  const placement = new Map<string, string>();
  const order: string[] = ['Highlights'];
  for (const v of o.views) for (const f of v.highlights.fields) if (!placement.has(f)) placement.set(f, 'Highlights');
  for (const v of o.views) {
    const lay = v.layout ? o.layouts[v.layout] : null;
    for (const s of lay?.sections ?? []) {
      if (!order.includes(s.label)) order.push(s.label);
      for (const f of s.columns.flat()) if (!placement.has(f.field)) placement.set(f.field, s.label);
    }
    for (const s of viewSections(o, v)) if (!order.includes(s)) order.push(s);
  }
  for (const v of o.views) for (const f of v.fields) if (!placement.has(f)) placement.set(f, 'On the page');
  if (!order.includes('On the page')) order.push('On the page');
  order.push('Not on any page');
  for (const f of Object.keys(o.fields)) if (!placement.has(f) && o.fields[f].source !== 'standard') placement.set(f, 'Not on any page');

  const views = (f: string) => o.views.filter((v) => v.fields.includes(f)).length;
  const header = ['#', 'Section', 'Field Label', 'API Name', 'Type', 'Defined in', 'Views showing it', 'Agreed layout', 'Confirmed', 'Open question', 'Answer', 'Picklist values'];
  const rows: RowSpec[] = [
    { cells: [`${meta.client} field dictionary: ${o.label}`], style: 'title' },
    { cells: [`Generated from ${meta.repo} · ${meta.branch} @ ${meta.commit.slice(0, 7)} on ${day(new Date().toISOString())}`], style: 'subtitle' },
    { cells: header, style: 'header' },
  ];
  let n = 0;
  for (const section of order) {
    const inSection = [...placement.entries()].filter(([, s]) => s === section).map(([f]) => f);
    const pending = current.filter((d) => !d.fieldApi && d.status !== 'withdrawn' && ((d.placement ?? '').toLowerCase() === section.toLowerCase() || (section === 'Highlights' && d.placement === 'highlights')));
    if (!inSection.length && !pending.length) continue;
    rows.push({ cells: ['', sectionTitle(section).toUpperCase()], style: 'section' });
    for (const f of inSection) {
      const def = o.fields[f];
      const d = byField.get(f);
      const qs = qsFor(f);
      rows.push({ cells: [
        ++n, section, def?.label ?? f, f, def?.type ?? '', def ? SOURCE_LABEL[def.source] : '', views(f),
        d ? DECISION_LABEL[d.status] : 'Check', [d?.note, d?.source].filter(Boolean).join(' · '),
        qs.filter((q) => q.status !== 'resolved').map((q) => q.question).join('\n'), qs.map((q) => q.answer).filter(Boolean).join('\n'),
        (def?.picklist ?? []).map(picklistLabel).join('; '),
      ] });
    }
    for (const d of pending) {
      const qs = qsFor(d.proposedLabel ?? '');
      rows.push({ cells: [++n, section, d.proposedLabel, '(new)', 'to build', '', 0, DECISION_LABEL[d.status], [d.note, d.source].filter(Boolean).join(' · '), qs.map((q) => q.question).join('\n'), qs.map((q) => q.answer).filter(Boolean).join('\n'), ''] });
    }
  }
  return [{ name: o.label, rows, widths: [5, 22, 30, 36, 16, 18, 10, 16, 48, 48, 36, 40], freezeBelowRow: 4, autofilterRow: 3 }];
}
